import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { VideoItem } from "@/types/video";
import { formatDuration, formatViews, getCategoryName } from "@/lib/videos";

export default function HeroCarousel({ slides }: { slides: VideoItem[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused, slides.length]);

  if (slides.length === 0) return null;

  const goTo = (next: number) => setIndex((next + slides.length) % slides.length);

  return (
    <section
      aria-label="精选短视频推荐"
      className="mx-auto w-full max-w-[1360px] px-4 md:px-8"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 40) goTo(index + (delta < 0 ? 1 : -1));
        touchStartX.current = null;
      }}
    >
      <div className="relative h-[420px] w-full overflow-hidden rounded-2xl bg-primary-950 md:h-[520px]">
        {slides.map((slide, slideIndex) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              slideIndex === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={slide.heroImage ?? slide.cover}
              alt={slide.title}
              title={`${slide.title} - 茄子视频精选推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/55 to-foreground-950/10"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent"></div>

            <div className="absolute inset-0 flex flex-col justify-end p-6 pb-20 md:p-12 md:pb-24 lg:p-14 lg:pb-24">
              <div
                className={`max-w-2xl ${slideIndex === index ? "animate-fade-up" : ""}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-accent-500 px-2.5 py-1 font-label text-[11px] font-bold text-foreground-950">
                    编辑精选
                  </span>
                  <span className="rounded-full border border-background-50/30 bg-background-50/10 px-2.5 py-1 text-[11px] text-background-100 backdrop-blur">
                    {getCategoryName(slide.categoryId)}
                  </span>
                  <span className="text-[11px] text-background-200/80">
                    {formatViews(slide.views)} 次播放 · {formatDuration(slide.duration)}
                  </span>
                </div>
                <h2 className="mt-4 font-heading text-2xl font-extrabold leading-tight text-background-50 md:text-4xl lg:text-[44px]">
                  {slide.title}
                </h2>
                <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200/85 md:text-[15px]">
                  {slide.description}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/video/${slide.id}`}
                    className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-800 transition-colors hover:bg-accent-300"
                  >
                    <i className="ri-play-fill text-base"></i>
                    立即播放
                  </Link>
                  <Link
                    to={`/category/${slide.categoryId}`}
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/35 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10"
                  >
                    浏览{getCategoryName(slide.categoryId)}
                    <i className="ri-arrow-right-line text-sm"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="absolute right-4 top-4 flex items-center gap-2 md:right-8 md:top-8">
          <span className="rounded-full bg-foreground-950/45 px-3 py-1 font-label text-[11px] tracking-[0.16em] text-background-100 backdrop-blur">
            {String(index + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </span>
        </div>

        <div className="absolute bottom-5 right-4 flex items-center gap-3 md:bottom-8 md:right-8">
          <div className="flex items-center gap-1.5">
            {slides.map((slide, dotIndex) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => goTo(dotIndex)}
                aria-label={`查看第 ${dotIndex + 1} 个推荐`}
                className={`h-1.5 cursor-pointer rounded-full transition-all ${
                  dotIndex === index ? "w-6 bg-accent-400" : "w-1.5 bg-background-50/45 hover:bg-background-50/70"
                }`}
              ></button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goTo(index - 1)}
              aria-label="上一个推荐"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 bg-foreground-950/30 text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              aria-label="下一个推荐"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 bg-foreground-950/30 text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}